// Mobile-First UX Component Library
// Transforms text inputs into touch-friendly choice-based interactions

export { 
  MobileChoiceSelector,
  MobileComparisonSelector,
  SwipeNavigator,
  TouchDecisionTree,
  PACEMobileBuilder,
  type ChoiceOption,
  type MobileSelectConfig
} from './MobileInteractionLibrary';

export { 
  MobilePromptBuilder 
} from './MobilePromptBuilder';

export { 
  AIOutputComparison,
  QuickRating 
} from './AIOutputComparison';

export { 
  TouchFriendlyMaya 
} from './TouchFriendlyMaya';

export { 
  ResponsiveLessonContainer 
} from './ResponsiveChoiceComponents';

// Utility types for mobile interactions
export interface MobileInteractionConfig {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  touchOptimized: boolean;
  gestureEnabled: boolean;
  voiceInputEnabled?: boolean;
}

export interface LearningProgress {
  completed: string[];
  current: string;
  achievements: string[];
  timeSpent: number;
}

export interface ChoiceBasedLesson {
  id: string;
  title: string;
  description: string;
  interactions: Array<{
    type: 'choice' | 'comparison' | 'builder' | 'decision-tree';
    data: any;
  }>;
  prerequisites: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

// Mobile-first design patterns
export const MOBILE_PATTERNS = {
  // Touch target sizes
  TOUCH_TARGETS: {
    minimum: '44px',
    comfortable: '48px',
    large: '56px'
  },
  
  // Spacing for mobile
  SPACING: {
    compact: '8px',
    comfortable: '16px',
    generous: '24px'
  },
  
  // Typography scales
  TYPOGRAPHY: {
    mobile: {
      h1: '1.5rem',
      h2: '1.25rem',
      h3: '1.125rem',
      body: '0.875rem',
      caption: '0.75rem'
    },
    tablet: {
      h1: '2rem',
      h2: '1.5rem',
      h3: '1.25rem',
      body: '1rem',
      caption: '0.875rem'
    }
  },
  
  // Animation presets
  ANIMATIONS: {
    tap: { scale: 0.95 },
    slide: { x: [-20, 0], opacity: [0, 1] },
    fade: { opacity: [0, 1] },
    bounce: { 
      scale: [0, 1.1, 1],
      transition: { type: 'spring', stiffness: 300 }
    }
  }
} as const;

// Pre-configured choice sets for common interactions
export const CHOICE_LIBRARIES = {
  communicationTones: [
    { id: 'professional', label: 'Professional', icon: '💼', description: 'Business-appropriate and formal' },
    { id: 'friendly', label: 'Friendly', icon: '😊', description: 'Warm and approachable' },
    { id: 'urgent', label: 'Urgent', icon: '⚡', description: 'Time-sensitive and direct' },
    { id: 'empathetic', label: 'Empathetic', icon: '💝', description: 'Understanding and supportive' },
    { id: 'confident', label: 'Confident', icon: '💪', description: 'Assertive and assured' },
    { id: 'collaborative', label: 'Collaborative', icon: '🤝', description: 'Team-oriented and inclusive' }
  ],
  
  audienceTypes: [
    { id: 'executives', label: 'Executives', icon: '👔', description: 'C-level decision makers' },
    { id: 'managers', label: 'Managers', icon: '👨‍💼', description: 'Middle management team' },
    { id: 'team-members', label: 'Team Members', icon: '👥', description: 'Direct reports and colleagues' },
    { id: 'clients', label: 'External Clients', icon: '🤝', description: 'External business partners' },
    { id: 'customers', label: 'End Customers', icon: '🛍️', description: 'Product/service users' },
    { id: 'stakeholders', label: 'Stakeholders', icon: '🎯', description: 'Interested parties' }
  ],
  
  contentTypes: [
    { id: 'email', label: 'Email', icon: '📧', description: 'Professional email communication' },
    { id: 'presentation', label: 'Presentation', icon: '📊', description: 'Slide deck or pitch' },
    { id: 'social-post', label: 'Social Media', icon: '📱', description: 'Social platform content' },
    { id: 'blog-article', label: 'Blog Article', icon: '✍️', description: 'Long-form written content' },
    { id: 'meeting-agenda', label: 'Meeting Agenda', icon: '📋', description: 'Structured meeting outline' },
    { id: 'proposal', label: 'Proposal', icon: '📄', description: 'Business proposal or pitch' }
  ],
  
  difficultyLevels: [
    { id: 'beginner', label: 'Beginner', icon: '🌱', description: 'New to AI communication' },
    { id: 'intermediate', label: 'Intermediate', icon: '🌿', description: 'Some AI experience' },
    { id: 'advanced', label: 'Advanced', icon: '🌳', description: 'Experienced AI user' },
    { id: 'expert', label: 'Expert', icon: '🏆', description: 'AI communication master' }
  ]
} as const;

// Accessibility helpers for mobile
export const ACCESSIBILITY_HELPERS = {
  // Screen reader friendly labels
  getAriaLabel: (interaction: string, step?: number, total?: number) => {
    const base = `${interaction} interaction`;
    return step && total ? `${base}, step ${step} of ${total}` : base;
  },
  
  // Focus management for touch interfaces
  manageFocus: (element: HTMLElement) => {
    element.setAttribute('tabindex', '0');
    element.focus();
  },
  
  // High contrast mode detection
  isHighContrast: () => window.matchMedia('(prefers-contrast: high)').matches,
  
  // Reduced motion preference
  prefersReducedMotion: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
} as const;